import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useForm, Controller } from 'react-hook-form';
import {
  DEFAULT_APPLICATION_FEE_CORPORATE,
  DEFAULT_APPLICATION_FEE_EMPLOYEE,
  DEFAULT_CORPORATE_DONATION_FEE_CAP_USD,
} from '@joindeed/calculate-fees';
import { useDispatch, useSelector } from 'react-redux';

import RetiredBox from 'retired/Box';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import { State } from 'reducers';

import * as Actions from './actions';
import { CompanyValidationInput } from './companyValidationInput';

export type ApplicationFees = {
  employee?: number;
  corporate?: number;
  match?: number;
  corporateDonationFeeCapUSD?: number;
};

const fees = [
  { name: 'employee', label: 'Employee Deed Fee (%)' },
  { name: 'corporate', label: 'Corporate Deed Fee (%)' },
  { name: 'match', label: 'Match Deed Fee (%)' },
] as const;

const defaultApplicationFees = {
  employee: DEFAULT_APPLICATION_FEE_EMPLOYEE,
  corporate: DEFAULT_APPLICATION_FEE_CORPORATE,
  match: DEFAULT_APPLICATION_FEE_CORPORATE,
  corporateDonationFeeCapUSD: DEFAULT_CORPORATE_DONATION_FEE_CAP_USD,
};

export const DeedFeeRates = () => {
  const dispatch = useDispatch();
  const organization = useSelector((state: State) => selectOrganization(state, true));
  const organizationName = organization?.name || '';
  const user = useSelector(selectUser);

  const isAdmin = user?.type === 'Admin';

  const applicationFees: Partial<ApplicationFees> | undefined =
    organization?.toJS?.().settings?.applicationFees;

  const { control, handleSubmit, formState } = useForm<Required<ApplicationFees>>({
    mode: 'all',
    defaultValues: { ...defaultApplicationFees, ...applicationFees },
  });

  const onSubmit = (paypalDeedFeesSelected: ApplicationFees) => {
    dispatch(
      Actions.saveAction(
        {
          id: organization?.id,
          // @NOTE-DP: we don't need to spread any other existing settings here, because we are passing updates through `dot.dot()` in the backend, so we wouldn't overwrite any other existing settings
          settings: { applicationFees: paypalDeedFeesSelected },
        },
        '-'
      )
    );
  };

  return (
    <RetiredBox style={{ paddingTop: 0, marginBottom: 30 }}>
      <h2>Deed fee rates</h2>

      <Grid container rowSpacing={2}>
        <Grid item lg={8} xs={12}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {fees.map(({ name, label }) => (
              <Controller
                rules={{
                  required: { value: true, message: 'Required' },
                  max: { value: 30, message: 'Maximum 30%' },
                  min: { value: 0, message: 'Minimum 0%' },
                  pattern: { value: /^\d*(\.\d+)?$/, message: 'Must be a number' },
                }}
                key={name}
                control={control}
                name={name}
                render={({ field, fieldState }) => (
                  <TextField
                    required
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9.]*' }}
                    sx={{ flex: '1 0 150px' }}
                    label={label}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    {...field}
                  />
                )}
              />
            ))}
          </Box>
          <Typography
            align="left"
            style={{
              fontSize: 12,
              padding: 10,
              color: '#00000080',
            }}
          >
            Employee donations percentage represents Deed fee on donations to PayPal nonprofits.
            PayPal will charge an additional fee on employee donations which will vary based on the
            nonprofit country.
          </Typography>
          <Typography
            align="left"
            style={{
              fontSize: 12,
              padding: 10,
              color: '#00000080',
            }}
          >
            Corporate disbursement percentage represents Total fee on disbursements to PayPal
            nonprofits, and includes both Deed and PayPal fees. Corporate disbursements include
            donation credit and corporate donations.
          </Typography>

          <Controller
            key="corporateDonationFeeCapUSD"
            control={control}
            name="corporateDonationFeeCapUSD"
            render={({ field }) => {
              const corporateDonationFeeCapUSD = field.value;
              const isCapped =
                corporateDonationFeeCapUSD >= 0 &&
                corporateDonationFeeCapUSD !== Number.POSITIVE_INFINITY;

              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={isCapped}
                      onChange={(event) =>
                        field.onChange(
                          event.target.checked ? DEFAULT_CORPORATE_DONATION_FEE_CAP_USD : -1
                        )
                      }
                    />
                  }
                  label={`Cap corporate donation fees to $${
                    isCapped
                      ? corporateDonationFeeCapUSD || DEFAULT_CORPORATE_DONATION_FEE_CAP_USD
                      : DEFAULT_CORPORATE_DONATION_FEE_CAP_USD
                  }`}
                />
              );
            }}
          />
        </Grid>

        <Grid
          item
          lg={4}
          xs={12}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
        >
          <CompanyValidationInput
            organizationName={organizationName}
            onSubmit={handleSubmit(onSubmit)}
            isFormValid={formState.isValid}
          />
        </Grid>
      </Grid>

      {isAdmin && user?.organization?.settings?.get('feeCoveredBy') && (
        <Grid
          item
          sm={12}
          lg={6}
          style={{
            marginTop: 10,
          }}
        >
          <p>
            Fees are covered by <strong>{user.organization.settings.get('feeCoveredBy')}</strong>
          </p>
        </Grid>
      )}
    </RetiredBox>
  );
};
